import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '../../lib/trpc';
import { DECIMAL_INPUT_PATTERN, oreToInputValue, parseMoneyInput } from '../settings/moneyInput';

const EMPTY_RULES: RuleItem[] = [];

function haveSameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/** Optional kr field → öre. Empty → null, invalid → NaN. Accepts "1 250,50". */
function parseOptionalOre(value: string): number | null {
  if (!value.trim()) return null;
  return parseMoneyInput(value) ?? Number.NaN;
}

/** Optional percent field → hundredths of a percent. Empty → null, invalid → NaN. Accepts "3,5". */
function parseOptionalRate(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return Number.NaN;
  return Math.round(Number(normalized) * 100);
}

function rateToInputValue(rate: number): string {
  return String(rate / 100).replace('.', ',');
}

type Loan = {
  id: string;
  name: string;
  totalAmount: number | null;
  interestRate: number | null;
  nextPaymentOverride: number | null;
};

type RuleItem = {
  id: string;
  pattern: string;
  categoryId: string;
  linkedLoanId?: string | null;
  linkedLoanName?: string | null;
};

type Props = {
  open: boolean;
  loan: Loan | null;
  onClose: () => void;
  onSaved: () => void;
};

export function LoanForm({ open, loan, onClose, onSaved }: Props) {
  const utils = trpc.useUtils();
  const rulesQuery = trpc.categoryRule.list.useQuery(undefined, {
    enabled: open,
    retry: false,
  });
  const categoriesQuery = trpc.category.listGroups.useQuery(undefined, {
    enabled: open,
    retry: false,
  });

  const createMutation = trpc.loan.create.useMutation({
    onSuccess: async () => {
      await utils.loan.list.invalidate();
      toast.success('Lånet skapades');
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.loan.update.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.loan.list.invalidate(), utils.loan.history.invalidate()]);
      toast.success('Lånet uppdaterades');
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  const [name, setName] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [nextPaymentOverride, setNextPaymentOverride] = useState('');
  const [ruleSearch, setRuleSearch] = useState('');
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const rules = (rulesQuery.data as RuleItem[] | undefined) ?? EMPTY_RULES;

  useEffect(() => {
    if (!open) {
      setName('');
      setTotalAmount('');
      setInterestRate('');
      setNextPaymentOverride('');
      setRuleSearch('');
      setSelectedRuleIds([]);
      return;
    }

    setName(loan?.name ?? '');
    setTotalAmount(loan?.totalAmount != null ? oreToInputValue(loan.totalAmount) : '');
    setInterestRate(loan?.interestRate != null ? rateToInputValue(loan.interestRate) : '');
    setNextPaymentOverride(
      loan?.nextPaymentOverride != null ? oreToInputValue(loan.nextPaymentOverride) : '',
    );
    setRuleSearch('');
  }, [loan, open]);

  useEffect(() => {
    if (!open) return;

    if (!loan) {
      setSelectedRuleIds((current) => (current.length === 0 ? current : []));
      return;
    }

    const nextSelectedRuleIds = rules
      .filter((rule) => rule.linkedLoanId === loan.id)
      .map((rule) => rule.id);

    setSelectedRuleIds((current) =>
      haveSameIds(current, nextSelectedRuleIds) ? current : nextSelectedRuleIds,
    );
  }, [loan, open, rules]);

  const categoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of categoriesQuery.data ?? []) {
      for (const category of group.categories) {
        map.set(category.id, `${group.name} / ${category.name}`);
      }
    }
    return map;
  }, [categoriesQuery.data]);

  const filteredRules = useMemo(() => {
    const searchValue = ruleSearch.trim().toLowerCase();
    if (!searchValue) return rules;

    return rules.filter((rule) => {
      const categoryLabel = categoryLabels.get(rule.categoryId)?.toLowerCase() ?? '';
      return (
        rule.pattern.toLowerCase().includes(searchValue) || categoryLabel.includes(searchValue)
      );
    });
  }, [categoryLabels, ruleSearch, rules]);

  const handleToggleRule = (ruleId: string) => {
    setSelectedRuleIds((current) =>
      current.includes(ruleId) ? current.filter((id) => id !== ruleId) : [...current, ruleId],
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (selectedRuleIds.length === 0) {
      toast.error('Välj minst en regel för att koppla lånet');
      return;
    }

    const parsedTotal = parseOptionalOre(totalAmount);
    const parsedRate = parseOptionalRate(interestRate);
    const parsedOverride = parseOptionalOre(nextPaymentOverride);

    if (parsedTotal != null && Number.isNaN(parsedTotal)) {
      toast.error('Ange ett giltigt totalbelopp');
      return;
    }
    if (parsedRate != null && Number.isNaN(parsedRate)) {
      toast.error('Ange en giltig ränta');
      return;
    }
    if (parsedOverride != null && Number.isNaN(parsedOverride)) {
      toast.error('Ange ett giltigt belopp för nästa betalning');
      return;
    }

    const payload = {
      name: name.trim(),
      totalAmount: parsedTotal,
      interestRate: parsedRate,
      nextPaymentOverride: parsedOverride,
      categoryRuleIds: selectedRuleIds,
    };

    if (loan) {
      updateMutation.mutate({ id: loan.id, ...payload });
      return;
    }

    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{loan ? 'Redigera lån' : 'Skapa lån'}</DialogTitle>
          <DialogDescription>
            Koppla en eller flera kategoriregler till lånet så att importerade transaktioner kan
            matchas automatiskt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="loan-name">Namn</Label>
            <Input
              id="loan-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="loan-total">Totalt belopp (kr)</Label>
              <Input
                id="loan-total"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                pattern={DECIMAL_INPUT_PATTERN}
                value={totalAmount}
                onChange={(event) => setTotalAmount(event.target.value)}
                className="mt-1"
                placeholder="t.ex. 500 000"
              />
            </div>
            <div>
              <Label htmlFor="loan-rate">Ränta (%)</Label>
              <Input
                id="loan-rate"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={interestRate}
                onChange={(event) => setInterestRate(event.target.value)}
                className="mt-1"
                placeholder="t.ex. 3,5"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="loan-override">Nästa betalning – manuell prognos (kr)</Label>
            <Input
              id="loan-override"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              pattern={DECIMAL_INPUT_PATTERN}
              value={nextPaymentOverride}
              onChange={(event) => setNextPaymentOverride(event.target.value)}
              className="mt-1"
              placeholder="Lämna tomt för automatisk beräkning"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Sätter du ett belopp här används det istället för den beräknade prognosen.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="loan-rule-search">Kopplade regler</Label>
              <span className="text-xs text-muted-foreground">{selectedRuleIds.length} valda</span>
            </div>
            <Input
              id="loan-rule-search"
              value={ruleSearch}
              onChange={(event) => setRuleSearch(event.target.value)}
              placeholder="Sök på regel eller kategori"
            />
            <div className="relative max-h-72 space-y-2 overflow-y-auto rounded-xl border border-border p-3">
              {rules.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga kategoriregler finns ännu.</p>
              ) : filteredRules.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">Inga regler matchar sökningen.</p>
              ) : (
                filteredRules.map((rule) => {
                  const isSelected = selectedRuleIds.includes(rule.id);
                  const isLinkedToOther =
                    rule.linkedLoanId != null && rule.linkedLoanId !== loan?.id;

                  return (
                    <div
                      key={rule.id}
                      className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${isLinkedToOther ? 'border-dashed bg-muted/30 opacity-60' : 'border-border'}`}
                    >
                      <Checkbox
                        aria-label={`Koppla regel ${rule.pattern}`}
                        checked={isSelected}
                        disabled={isLinkedToOther}
                        onCheckedChange={() => handleToggleRule(rule.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm text-foreground">{rule.pattern}</div>
                        <div className="text-xs text-muted-foreground">
                          {categoryLabels.get(rule.categoryId) ?? 'Okänd kategori'}
                        </div>
                        {isLinkedToOther && rule.linkedLoanName && (
                          <div className="mt-1 text-xs text-warning">
                            Redan kopplad till "{rule.linkedLoanName}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {loan ? 'Spara' : 'Skapa'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
